/**
 * Thumbnail generation, IN THE BROWSER THAT IS UPLOADING.
 *
 * The API has no image-processing library, and adding one so it can decode
 * arbitrary untrusted image formats in-process is a large attack surface to buy
 * a 40 KB file. The uploading browser has already decoded the bitmap — it is on
 * screen in the composer tray — so a canvas downscale costs it a few
 * milliseconds and costs the server nothing but a `PUT`.
 *
 * The server does not trust the result: it caps the size, checks the magic
 * number against the declared type, names the object itself, and refuses to
 * overwrite a thumbnail that is already there.
 */

/** Longest edge of the generated image. Comfortably covers a 2x retina cell. */
const MAX_EDGE = 480;

/** Below this the original is already small enough to serve as its own thumb. */
const SKIP_BELOW_BYTES = 96 * 1024;

/** Must stay under the API's 256 KB ceiling with room to spare. */
const MAX_OUTPUT_BYTES = 192 * 1024;

const QUALITY = 0.72;

const SUPPORTED = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif']);

export interface GeneratedThumbnail {
  mimeType: 'image/webp' | 'image/jpeg';
  dataBase64: string;
  /** Dimensions of the SOURCE image — what a layout needs to reserve space. */
  width: number;
  height: number;
}

function toBase64(bytes: Uint8Array): string {
  // Chunked: `String.fromCharCode(...bytes)` on a 100 KB array blows the
  // argument limit and throws RangeError in every engine.
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
}

const encode = (canvas: HTMLCanvasElement, type: string): Promise<Blob | null> =>
  new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));

/** `null` whenever a thumbnail would not help — the caller simply skips it. */
export async function renderThumbnail(file: File): Promise<GeneratedThumbnail | null> {
  if (!SUPPORTED.has(file.type) || file.size < SKIP_BELOW_BYTES) return null;
  if (typeof createImageBitmap !== 'function') return null;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  const { width, height } = bitmap;
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const context = canvas.getContext('2d');
  if (context === null) {
    bitmap.close();
    return null;
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  // WebP first — roughly a third of the JPEG bytes at this quality. Safari
  // below 14 returns a PNG for an unknown type, which the check catches.
  let blob = await encode(canvas, 'image/webp');
  if (blob === null || blob.type !== 'image/webp') blob = await encode(canvas, 'image/jpeg');
  if (blob === null || blob.size === 0 || blob.size > MAX_OUTPUT_BYTES) return null;

  return {
    mimeType: blob.type === 'image/webp' ? 'image/webp' : 'image/jpeg',
    dataBase64: toBase64(new Uint8Array(await blob.arrayBuffer())),
    width,
    height,
  };
}
